import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery } from '@apollo/client'
import { GET_DRIVER_DELIVERIES } from '../graphql/driver-queries'
import { MARK_DELIVERED } from '../graphql/driver-mutations'
import {
  GET_MY_DELIVERY_RUNS,
  REORDER_DELIVERY_RUN,
  START_DELIVERY_RUN,
  UNDO_START_DELIVERY_RUN,
} from '../graphql/delivery-run'
import { DeliveryRunDetail, Order, OrderStatus } from '../types/order'
import { auth } from '../lib/auth'
import { formatTime, localDayBoundsUtc } from '../lib/format'
import {
  addressOf,
  canStartRun,
  deliveredAt,
  formatEta,
  hasAddress,
  mapsUrl,
  openStops,
  runMapsUrl,
  STORE_DOOR,
  stopNumbers,
  stopsInOrder,
  stopsNotReady,
} from '../lib/delivery'
import { useStatusOverrides } from '../hooks/useStatusOverrides'
import {
  ChevronDownIcon,
  ChevronRightIcon,
  HomeIcon,
  MapPinIcon,
  PhoneIcon,
  TruckIcon,
} from '@heroicons/react/24/outline'

// A driver looks at this between doors, not continuously — the same restraint the
// delivery board applies (every logged-in device polls the same API). Local status
// overrides keep a tap instant regardless of the interval.
const DRIVER_POLL_MS = 30_000

// Must EXCEED the poll interval, not merely the in-flight window: a post-mutation
// refetch deduped onto an already-in-flight poll caches a pre-mutation snapshot, and
// only the next poll issued after the mutation corrects it. A shorter TTL would let a
// started stop snap back to "Ready" until that poll lands. Derived from the interval so
// the two cannot drift.
const STATUS_OVERRIDE_TTL_MS = DRIVER_POLL_MS + 15_000

// How long "Mark delivered" stays armed after the first tap. Long enough to confirm
// deliberately at the door, short enough that a stray tap cannot linger and complete a
// delivery on the driver's next tap somewhere else on the card.
const CONFIRM_ARM_MS = 4_000

// Arrival estimates are written a moment AFTER a route mutation returns (contract,
// "Arrival estimates"), so its response may still carry the old or null ETA. One
// follow-up read of my routes picks up the fresh estimates — and shows a route whose last
// stop was just delivered as done — without waiting up to a whole poll interval.
const ETA_SETTLE_MS = 5_000

// Kitchen progress of a stop, in the driver's words: it decides whether a planned route
// can start, and what "Coming up" is still waiting on.
const KITCHEN_STATUS_CHIPS: Partial<Record<OrderStatus, { label: string; className: string }>> = {
  READY: { label: 'Ready', className: 'bg-green-100 text-green-800' },
  PREPARING: { label: 'Being made', className: 'bg-orange-100 text-orange-800' },
  CONFIRMED: { label: 'Waiting', className: 'bg-blue-100 text-blue-800' },
  SCHEDULED: { label: 'Scheduled', className: 'bg-cyan-100 text-cyan-800' },
}

const sortKey = (order: Order): number => {
  const parsed = order.scheduled_for ? new Date(order.scheduled_for).getTime() : NaN
  return Number.isNaN(parsed) ? 0 : parsed
}

const completedSortKey = (order: Order): number => {
  const time = order.current_status_since || order.scheduled_for
  const parsed = time ? new Date(time).getTime() : NaN
  return Number.isNaN(parsed) ? 0 : parsed
}

const shortNumber = (order: Order): string => `#${order.order_number.slice(-5)}`

// Order-insensitive: a reorder draft stays valid while the SET of open stops is the same.
const sameIds = (a: number[], b: number[]): boolean => {
  if (a.length !== b.length) return false
  const ids = new Set(a)
  return b.every(id => ids.has(id))
}

const byRunId = (a: DeliveryRunDetail, b: DeliveryRunDetail) => a.id - b.id

const DriverPage: React.FC = () => {
  const { applyOverrides, recordOverride } = useStatusOverrides(STATUS_OVERRIDE_TTL_MS)

  // auth.getUser() stores id as a string (localStorage JSON); delivered_by_user.id comes
  // back as an Int. Normalise once here so "mine" is a plain === comparison everywhere.
  const myId = useMemo(() => {
    const user = auth.getUser()
    return user ? Number(user.id) : null
  }, [])

  const [showUpcoming, setShowUpcoming] = useState(false)

  // One lock for every action on the page, keyed `run:<id>` / `order:<id>`: the ref is the
  // synchronous guard (React 18 batches state updates, so a state-only guard wouldn't see
  // a same-render double-tap); the state mirror drives the UI. Keying per route / per
  // order keeps every other button tappable while one call is in flight.
  const processingRef = useRef<Set<string>>(new Set())
  const [processingKeys, setProcessingKeys] = useState<Set<string>>(new Set())

  // Two-step confirmation for the one irreversible action on this page.
  const [armedOrderId, setArmedOrderId] = useState<number | null>(null)
  const armTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // "Change order" drafts: the edited sequence of open stop ids, keyed by route id. A
  // route with a draft is in edit mode.
  const [reorderDrafts, setReorderDrafts] = useState<Record<number, number[]>>({})

  const disarm = useCallback(() => {
    if (armTimerRef.current) clearTimeout(armTimerRef.current)
    armTimerRef.current = null
    setArmedOrderId(null)
  }, [])

  useEffect(() => () => { if (armTimerRef.current) clearTimeout(armTimerRef.current) }, [])

  // Recomputed every render, never memoized at mount: a phone left on the driver screen
  // crosses midnight mid-shift, and a frozen window would keep querying yesterday.
  const { scheduledFrom: todayStartIso, scheduledTo } = localDayBoundsUtc()
  const todayStartMs = new Date(todayStartIso).getTime()

  // Start the window a day early, exactly as the delivery board does. A run that goes out
  // at 23:50 is still out at 00:05, and a today-only window would drop it off the screen
  // with no way left to mark it delivered. Stepping the local date back by one (rather
  // than subtracting 24h) keeps this correct across a DST change.
  const lookbackStart = new Date(todayStartMs)
  lookbackStart.setDate(lookbackStart.getDate() - 1)

  const { data, loading, error, refetch } = useQuery(GET_DRIVER_DELIVERIES, {
    variables: {
      from: lookbackStart.toISOString(),
      to: scheduledTo,
    },
    fetchPolicy: 'cache-and-network',
    pollInterval: DRIVER_POLL_MS,
    notifyOnNetworkStatusChange: true,
  })

  // A separate document on purpose (see graphql/delivery-run.ts): against an API without
  // routes only this query fails, and the rest of the screen keeps working.
  const {
    data: runsData,
    loading: runsLoading,
    error: runsError,
    refetch: refetchRuns,
  } = useQuery(GET_MY_DELIVERY_RUNS, {
    fetchPolicy: 'cache-and-network',
    pollInterval: DRIVER_POLL_MS,
    notifyOnNetworkStatusChange: true,
  })

  // Every refetch here is best-effort convergence, never a source of truth: the polls
  // re-run on their own and status changes are shielded locally. Swallow rejections so a
  // dead spot can neither surface as a false failure nor escape as an unhandled rejection.
  const settle = useCallback(
    () => Promise.all([refetch().catch(() => undefined), refetchRuns().catch(() => undefined)]),
    [refetch, refetchRuns]
  )

  // Critical on a phone: the screen sleeps in a pocket and the poll timer is throttled or
  // frozen while backgrounded, so waking the tab is the moment the list is most stale.
  // visibilitychange as well as focus: iOS Safari routinely resumes from the lock screen
  // or an app switch (Maps, the dialer — both launched FROM this page) with only a
  // visibilitychange, no window focus event.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') settle()
    }
    window.addEventListener('focus', settle)
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      window.removeEventListener('focus', settle)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [settle])

  // Restarted per action, so a quick series of taps still ends with one read AFTER the
  // last mutation — the one whose estimates matter.
  const runsRefetchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const scheduleRunsRefetch = useCallback(() => {
    if (runsRefetchTimerRef.current) clearTimeout(runsRefetchTimerRef.current)
    runsRefetchTimerRef.current = setTimeout(() => {
      runsRefetchTimerRef.current = null
      refetchRuns().catch(() => undefined)
    }, ETA_SETTLE_MS)
  }, [refetchRuns])

  useEffect(() => () => { if (runsRefetchTimerRef.current) clearTimeout(runsRefetchTimerRef.current) }, [])

  const [markDelivered] = useMutation(MARK_DELIVERED)
  const [startDeliveryRun] = useMutation(START_DELIVERY_RUN)
  const [undoStartDeliveryRun] = useMutation(UNDO_START_DELIVERY_RUN)
  const [reorderDeliveryRun] = useMutation(REORDER_DELIVERY_RUN)

  const orders: Order[] = useMemo(
    () => applyOverrides((data?.driverDeliveries ?? []) as Order[]),
    [data, applyOverrides]
  )

  // Stops are shielded exactly like the lists: a poll snapshotted before Start would
  // otherwise drop freshly started stops back to "Ready" for a round trip.
  const allRuns: DeliveryRunDetail[] = useMemo(
    () =>
      ((runsData?.myDeliveryRuns ?? []) as DeliveryRunDetail[]).map(r => ({
        ...r,
        stops: applyOverrides(r.stops),
      })),
    [runsData, applyOverrides]
  )

  // The route on the road first — it is what the driver is doing right now — then the
  // planned ones. The API also returns routes completed in the last 24h; those are done.
  const myRuns = useMemo(
    () => [
      ...allRuns.filter(r => r.status === 'ACTIVE').sort(byRunId),
      ...allRuns.filter(r => r.status === 'PLANNED').sort(byRunId),
    ],
    [allRuns]
  )

  // Every order already in one of my routes is shown there, so "Coming up" must not list
  // it a second time.
  const routedOrderIds = useMemo(
    () => new Set(allRuns.flatMap(r => r.stops.map(s => s.id))),
    [allRuns]
  )

  // Out with ME but in none of my routes: claimed before the API had routes (a claim made
  // after that became a route of one). Without this list such an order would show nowhere
  // on this screen and only the counter could complete it. Waits for the routes answer —
  // or its failure, on an API without routes — so a routed stop never flashes here first.
  const outWithMeUnrouted = useMemo(
    () =>
      runsData || runsError
        ? orders
            .filter(
              o =>
                o.status === 'IN_DELIVERY' && o.delivered_by_user?.id === myId && !routedOrderIds.has(o.id)
            )
            .sort((a, b) => sortKey(a) - sortKey(b))
        : [],
    [orders, myId, routedOrderIds, runsData, runsError]
  )

  const withOthers = useMemo(
    () =>
      orders
        .filter(o => o.status === 'IN_DELIVERY' && o.delivered_by_user?.id !== myId)
        .sort((a, b) => sortKey(a) - sortKey(b)),
    [orders, myId]
  )

  const upcoming = useMemo(
    () =>
      orders
        .filter(
          o =>
            (o.status === 'CONFIRMED' || o.status === 'PREPARING' || o.status === 'READY') &&
            !routedOrderIds.has(o.id)
        )
        .sort((a, b) => sortKey(a) - sortKey(b)),
    [orders, routedOrderIds]
  )

  // Personal tally, not the shop total: driverDeliveries is a shared team scope, and two
  // drivers each doing 5 runs must not both read "10". Orders completed by FOH's board
  // fallback keep their claim stamp, so they still count for the driver who carried them;
  // never-claimed orders count for nobody, which is honest. The query looks back to
  // yesterday so nothing in flight vanishes at midnight, but "today" still means today —
  // re-narrow on the completion time.
  const deliveredTodayCount = useMemo(
    () =>
      orders.filter(
        o =>
          o.status === 'COMPLETED' &&
          o.delivered_by_user?.id === myId &&
          completedSortKey(o) >= todayStartMs
      ).length,
    [orders, myId, todayStartMs]
  )

  // A draft is only meaningful for the exact stops it was made from: reorderDeliveryRun
  // must receive every stop still out and nothing else. When a poll shows one delivered
  // or cancelled (or the route left ACTIVE), the draft is wrong — discard it rather than
  // send a list the API will refuse.
  useEffect(() => {
    setReorderDrafts(prev => {
      const stale = Object.keys(prev)
        .map(Number)
        .filter(runId => {
          const r = myRuns.find(x => x.id === runId)
          return !r || r.status !== 'ACTIVE' || !sameIds(openStops(r.stops).map(s => s.id), prev[runId])
        })
      if (stale.length === 0) return prev
      const next = { ...prev }
      stale.forEach(runId => delete next[runId])
      return next
    })
  }, [myRuns])

  const dropDraft = (runId: number) =>
    setReorderDrafts(prev => {
      if (!(runId in prev)) return prev
      const next = { ...prev }
      delete next[runId]
      return next
    })

  // Shared runner for every mutation on this page — same shape as the board's advance().
  const withLock = async (key: string, action: () => Promise<void>) => {
    if (processingRef.current.has(key)) return
    processingRef.current.add(key)
    setProcessingKeys(prev => new Set(prev).add(key))
    try {
      await action()
    } catch {
      // Already on screen: the global errorLink in lib/apollo.ts toasts every GraphQL
      // error (the route message prefixes included), so no toast here. Every rejection
      // means this screen is stale — the kitchen isn't done, an operator changed the plan,
      // a stop was cancelled — so refetch both lists. Fire-and-forget: settle() swallows
      // rejections, and holding the lock for an extra round trip would only slow the
      // driver down.
      void settle()
    } finally {
      processingRef.current.delete(key)
      setProcessingKeys(prev => {
        const nextSet = new Set(prev)
        nextSet.delete(key)
        return nextSet
      })
    }
  }

  const onDeliver = (orderId: number) => {
    disarm()
    return withLock(`order:${orderId}`, async () => {
      const { data: result } = await markDelivered({ variables: { id: orderId } })
      const updated = result?.markDelivered as Order | undefined
      // No immediate refetch (per the API contract note). The mutation result has already
      // merged into the normalized cache — the stop is the same Order object in both
      // queries — and the override shields against any stale poll snapshot for its TTL.
      // The result carries status + current_status_since + delivered_by_user together, so
      // the shield covers the claim stamp as well as the status.
      if (updated) recordOverride(orderId, updated)
      // The route around the stop did move on, though: the remaining estimates recompute
      // from this door, and the last stop completes the route.
      scheduleRunsRefetch()
    })
  }

  // One tap: Start is freely reversible (Undo start), and every stop was READY.
  const onStartRun = (runId: number) =>
    withLock(`run:${runId}`, async () => {
      const { data: result } = await startDeliveryRun({ variables: { id: runId } })
      const updated = result?.startDeliveryRun as DeliveryRunDetail | undefined
      // All stops went READY → IN_DELIVERY in one transaction; shield each of them.
      updated?.stops.forEach(stop => recordOverride(stop.id, stop))
      scheduleRunsRefetch()
    })

  // One tap, like Start: the escape hatch for a Start tapped too early. Putting a
  // confirmation in front of it would make the mis-tap harder to fix than to make.
  const onUndoStart = (runId: number) => {
    disarm()
    return withLock(`run:${runId}`, async () => {
      const { data: result } = await undoStartDeliveryRun({ variables: { id: runId } })
      const updated = result?.undoStartDeliveryRun as DeliveryRunDetail | undefined
      // Every stop back to READY in one transaction; a stale poll must not re-open them.
      updated?.stops.forEach(stop => recordOverride(stop.id, stop))
    })
  }

  const onSaveOrder = (runId: number, orderIds: number[]) =>
    withLock(`run:${runId}`, async () => {
      // The response carries the new positions into the normalized cache.
      await reorderDeliveryRun({ variables: { id: runId, orderIds } })
      dropDraft(runId)
      scheduleRunsRefetch()
    })

  const startEditing = (run: DeliveryRunDetail) =>
    setReorderDrafts(prev => ({ ...prev, [run.id]: openStops(run.stops).map(s => s.id) }))

  const moveStop = (runId: number, index: number, delta: -1 | 1) =>
    setReorderDrafts(prev => {
      const draft = prev[runId]
      const target = index + delta
      if (!draft || target < 0 || target >= draft.length) return prev
      const next = [...draft]
      ;[next[index], next[target]] = [next[target], next[index]]
      return { ...prev, [runId]: next }
    })

  const arm = (orderId: number) => {
    if (armTimerRef.current) clearTimeout(armTimerRef.current)
    setArmedOrderId(orderId)
    armTimerRef.current = setTimeout(() => {
      armTimerRef.current = null
      setArmedOrderId(null)
    }, CONFIRM_ARM_MS)
  }

  // Rendered via plain function calls, not <Card /> components declared here: a component
  // type declared inside the parent gets a new identity every render, so React would
  // unmount and remount every card on each poll tick — killing taps mid-gesture.
  const renderTimeChip = (order: Order) =>
    order.is_preorder && order.scheduled_for ? (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-cyan-600 text-white text-[10px] font-black uppercase tracking-wide whitespace-nowrap">
        Pre-order {formatTime(order.scheduled_for)}
      </span>
    ) : (
      // Never re-derived from scheduled_for/created_at — is_preorder is server-computed.
      <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 text-[10px] font-black uppercase tracking-wide whitespace-nowrap">
        ASAP
      </span>
    )

  const renderKitchenChip = (order: Order) => {
    const chip = KITCHEN_STATUS_CHIPS[order.status]
    return (
      <span
        className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wide whitespace-nowrap ${
          chip?.className ?? 'bg-gray-100 text-gray-600'
        }`}
      >
        {chip?.label ?? order.status}
      </span>
    )
  }

  const renderSectionHeader = (title: string, count: number, accent: string) => (
    <h2 className={`text-sm font-black uppercase tracking-wide mb-3 ${accent}`}>
      {title} ({count})
    </h2>
  )

  const renderEmpty = (label: string) => (
    <div className="flex items-center justify-center py-8 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
      <span className="text-[11px] font-bold text-gray-400 uppercase tracking-widest">{label}</span>
    </div>
  )

  // An open stop of my active route: everything needed at the door. Only ever fed stops
  // from myDeliveryRuns, which the API scopes to routes assigned to me — so "Mark
  // delivered" here is always the driver's own delivery.
  // `run` is null only for an order out with me that is in no route (`outWithMeUnrouted`):
  // it gets the door actions without route context.
  const renderStopCard = (run: DeliveryRunDetail | null, order: Order) => {
    const isProcessing = processingKeys.has(`order:${order.id}`)
    const isArmed = armedOrderId === order.id
    const { line1, line2 } = addressOf(order)
    const eta = run ? formatEta(order.delivery_eta_at) : null
    // Counted like stops_total (lib/delivery.ts stopNumbers), never the raw position.
    const stopNumber = run ? stopNumbers(run.stops).get(order.id) : undefined

    return (
      <div key={order.id} className="bg-white rounded-xl shadow-sm border-l-4 border-indigo-500">
        <div className="p-4">
          {run && (
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mb-2">
              <span className="text-xs font-black uppercase tracking-wide text-indigo-600">
                Stop {stopNumber ?? '–'} of {run.stops_total}
              </span>
              {order.delivery_stops_before === 0 && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-green-600 text-white text-[10px] font-black uppercase tracking-wide whitespace-nowrap">
                  Next
                </span>
              )}
              {/* An estimate, never the promise — and null means "no estimate", so it
                  renders nothing rather than anything that reads as late. */}
              {eta && <span className="ml-auto text-sm font-black text-gray-900">{eta}</span>}
            </div>
          )}

          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mb-2">
            <span className="text-2xl font-black text-gray-900 leading-none">{shortNumber(order)}</span>
            {renderTimeChip(order)}
          </div>

          {order.customer_name && (
            <div className="text-base font-bold text-gray-800 truncate">{order.customer_name}</div>
          )}

          {hasAddress(order) && (
            <div className="mt-2 text-sm text-gray-700 bg-gray-50 rounded-lg p-3">
              {line1 && <div className="font-bold">{line1}</div>}
              {line2 && <div>{line2}</div>}
            </div>
          )}

          {order.delivery_instructions && (
            <div className="mt-2 text-sm font-bold text-blue-800 bg-blue-50 px-3 py-2 rounded-md">
              {order.delivery_instructions}
            </div>
          )}

          {hasAddress(order) && (
            <a
              href={mapsUrl(order)}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 w-full py-3 flex items-center justify-center gap-2 bg-gray-900 hover:bg-black active:scale-95 text-white text-sm font-black rounded-lg uppercase tracking-wide transition-all"
            >
              <MapPinIcon className="w-5 h-5" />
              Route
            </a>
          )}

          {order.customer_phone && (
            <a
              href={`tel:${order.customer_phone}`}
              className="mt-2 w-full py-3 flex items-center justify-center gap-2 bg-white border-2 border-gray-200 hover:bg-gray-50 active:scale-95 text-gray-800 text-sm font-black rounded-lg transition-all"
            >
              <PhoneIcon className="w-5 h-5" />
              {order.customer_phone}
              {/* Uber Eats relay number: the driver keys this code in after the prompt.
                  The tel: link stays on the bare number (DTMF is unreliable across phones). */}
              {order.customer_phone_access_code && (
                <span className="font-semibold text-gray-600">· code {order.customer_phone_access_code}</span>
              )}
            </a>
          )}

          {/* Two-step on purpose: completing a delivery is the one action on this screen
              that cannot be undone from here, and the driver is tapping a phone
              one-handed at a door. The first tap only arms it. */}
          <button
            onClick={() => (isArmed ? onDeliver(order.id) : arm(order.id))}
            disabled={isProcessing}
            className={`mt-3 w-full py-4 text-white text-sm font-black rounded-lg uppercase tracking-wide transition-all ${
              isProcessing
                ? 'bg-green-500 cursor-not-allowed'
                : isArmed
                  ? 'bg-green-800 ring-4 ring-green-300 animate-pulse'
                  : 'bg-green-700 hover:bg-green-800 active:scale-95'
            }`}
          >
            {isProcessing ? 'Updating…' : isArmed ? 'Tap again to confirm' : 'Mark delivered'}
          </button>
        </div>
      </div>
    )
  }

  // A stop that is done: kept in the sequence so "Stop 3 of 4" still adds up, but muted
  // and without actions. The last branch only shows while a status override and a poll
  // briefly disagree — an active route's stops are otherwise out, delivered or cancelled.
  const renderSettledStopRow = (run: DeliveryRunDetail, order: Order) => {
    const label =
      order.status === 'COMPLETED'
        ? 'Delivered'
        : order.status === 'CANCELLED' || order.status === 'REFUNDED'
          ? 'Cancelled'
          : order.status
    // A cancelled stop no longer counts, so it carries no stop number.
    const stopNumber = stopNumbers(run.stops).get(order.id)
    // The real time of the delivery, where an open stop shows its estimate.
    const delivered = deliveredAt(order)
    return (
      <div
        key={order.id}
        className="bg-gray-50 rounded-xl border border-gray-200 px-3 py-2 flex items-center gap-2 text-sm font-bold text-gray-400"
      >
        <span className="flex-1 min-w-0">
          {stopNumber !== undefined && `Stop ${stopNumber} · `}
          {shortNumber(order)} · {label}
        </span>
        {delivered && <span className="whitespace-nowrap">{delivered}</span>}
      </div>
    )
  }

  // ▲ / ▼ rather than drag-and-drop: HTML5 drag events don't fire on touch.
  const renderReorderEditor = (run: DeliveryRunDetail, draft: number[]) => {
    const stopById = new Map(run.stops.map(s => [s.id, s]))
    const rows = draft.map(id => stopById.get(id)).filter((s): s is Order => Boolean(s))
    const isSaving = processingKeys.has(`run:${run.id}`)
    const arrowClass =
      'min-h-[44px] min-w-[44px] flex items-center justify-center rounded-lg border-2 border-gray-200 bg-white text-lg font-black text-gray-800 active:scale-95 transition-all disabled:opacity-30 disabled:active:scale-100'

    return (
      <div className="bg-white rounded-xl shadow-sm border-l-4 border-indigo-500 p-4">
        <div className="text-xs font-black uppercase tracking-wide text-indigo-600 mb-3">Change order</div>
        <div className="space-y-2">
          {rows.map((stop, index) => (
            <div key={stop.id} className="flex items-center gap-2">
              <div className="flex-1 min-w-0">
                <div className="text-base font-black text-gray-900">{shortNumber(stop)}</div>
                <div className="text-sm text-gray-600 truncate">{addressOf(stop).line1}</div>
              </div>
              <button
                onClick={() => moveStop(run.id, index, -1)}
                disabled={index === 0 || isSaving}
                aria-label={`Move ${shortNumber(stop)} up`}
                className={arrowClass}
              >
                ▲
              </button>
              <button
                onClick={() => moveStop(run.id, index, 1)}
                disabled={index === rows.length - 1 || isSaving}
                aria-label={`Move ${shortNumber(stop)} down`}
                className={arrowClass}
              >
                ▼
              </button>
            </div>
          ))}
        </div>
        <div className="mt-4 grid grid-cols-2 gap-2">
          <button
            onClick={() => dropDraft(run.id)}
            disabled={isSaving}
            className="min-h-[44px] bg-white border-2 border-gray-200 hover:bg-gray-50 active:scale-95 text-gray-800 text-sm font-black rounded-lg uppercase tracking-wide transition-all disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={() => onSaveOrder(run.id, draft)}
            disabled={isSaving}
            className={`min-h-[44px] text-white text-sm font-black rounded-lg uppercase tracking-wide transition-all ${
              isSaving ? 'bg-indigo-400 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700 active:scale-95'
            }`}
          >
            {isSaving ? 'Saving…' : 'Save order'}
          </button>
        </div>
      </div>
    )
  }

  const renderActiveRun = (run: DeliveryRunDetail) => {
    const open = openStops(run.stops)
    const draft = reorderDrafts[run.id]
    const isRunLocked = processingKeys.has(`run:${run.id}`)
    // When the driver is expected back at the store — the route's last location. Written
    // by the API a moment after Start / a delivery / a re-sequence, so it arrives with the
    // follow-up read; null means no estimate and shows nothing.
    const back = formatEta(run.expected_back_at)

    return (
      <div key={run.id} className="rounded-xl bg-indigo-50 border border-indigo-100 p-3 space-y-3">
        <div>
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="text-lg font-black text-gray-900">Route {run.id}</span>
            <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-indigo-600 text-white text-[10px] font-black uppercase tracking-wide whitespace-nowrap">
              On the road
            </span>
          </div>
          <div className="mt-1 text-xs font-bold text-gray-500">
            {run.started_at && <>Departed {formatTime(run.started_at)} · </>}
            {run.stops_delivered} of {run.stops_total} delivered
            {back && <> · back {back}</>}
          </div>
        </div>

        {/* One Maps trip through every stop still out, in stop order. Skipped when none of
            them has an address — the link would have no destination. */}
        {open.some(hasAddress) && (
          <a
            href={runMapsUrl(open)}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full py-3 flex items-center justify-center gap-2 bg-gray-900 hover:bg-black active:scale-95 text-white text-sm font-black rounded-lg uppercase tracking-wide transition-all"
          >
            <MapPinIcon className="w-5 h-5" />
            Route — all stops
          </a>
        )}

        {draft
          ? renderReorderEditor(run, draft)
          : stopsInOrder(run.stops).map(stop =>
              stop.status === 'IN_DELIVERY' ? renderStopCard(run, stop) : renderSettledStopRow(run, stop)
            )}

        {/* The store closes the route (owner 2026-09-30): the same row the board shows, with
            the driver's own expected return. Stays while a re-sequence is being drafted — the
            time it shows is the one before the change; the saved order brings the new one. */}
        <div className="flex items-center gap-2.5 bg-white border-2 border-dashed border-indigo-300 rounded-xl p-3">
          <span className="w-8 h-8 flex-shrink-0 rounded-lg bg-indigo-100 text-indigo-800 flex items-center justify-center">
            <HomeIcon className="w-[18px] h-[18px]" />
          </span>
          <span className="flex-1 min-w-0 flex flex-col">
            <span className="text-sm font-black text-gray-900">Back at the store</span>
            <span className="text-xs font-semibold text-gray-600">{STORE_DOOR}</span>
          </span>
          {back && <span className="text-sm font-black text-gray-900 whitespace-nowrap">{back}</span>}
        </div>

        {!draft && open.length >= 2 && (
          <button
            onClick={() => startEditing(run)}
            disabled={isRunLocked}
            className="w-full min-h-[44px] bg-white border-2 border-gray-200 hover:bg-gray-50 active:scale-95 text-gray-800 text-sm font-black rounded-lg uppercase tracking-wide transition-all disabled:opacity-50"
          >
            Change order
          </button>
        )}

        {/* The API refuses it once anything is delivered, so it is only offered before.
            stops_delivered alone lags: Mark delivered returns the order, not the route, so
            the count stays stale until the next read — the shielded stop status is current. */}
        {run.status === 'ACTIVE' &&
          run.stops_delivered === 0 &&
          !run.stops.some(stop => stop.status === 'COMPLETED') && (
          <button
            onClick={() => onUndoStart(run.id)}
            disabled={isRunLocked}
            className="w-full py-2 text-xs font-bold text-gray-400 hover:text-gray-700 disabled:text-gray-300"
          >
            {isRunLocked ? 'Updating…' : 'Undo start — back to planned'}
          </button>
        )}
      </div>
    )
  }

  // Planned for me by the operators: what is in it and whether the kitchen is done. The
  // driver never decides what is in a route, so the only action is Start.
  const renderPlannedRun = (run: DeliveryRunDetail) => {
    const stops = stopsInOrder(run.stops)
    const numbers = stopNumbers(run.stops)
    const isStarting = processingKeys.has(`run:${run.id}`)

    return (
      <div key={run.id} className="bg-white rounded-xl shadow-sm border-l-4 border-gray-400">
        <div className="p-4">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="text-lg font-black text-gray-900">Route {run.id}</span>
            <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-gray-200 text-gray-700 text-[10px] font-black uppercase tracking-wide whitespace-nowrap">
              Planned
            </span>
            <span className="ml-auto text-xs font-bold text-gray-500">
              {stops.length} {stops.length === 1 ? 'stop' : 'stops'}
            </span>
          </div>

          {stops.length > 0 && (
            <div className="mt-3 divide-y divide-gray-100">
              {stops.map(stop => {
                const { line1, line2 } = addressOf(stop)
                return (
                  <div key={stop.id} className="py-2 flex items-start gap-3">
                    <span className="w-7 h-7 flex-shrink-0 rounded-full bg-gray-100 text-gray-700 text-sm font-black flex items-center justify-center">
                      {numbers.get(stop.id) ?? '–'}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        <span className="text-sm font-black text-gray-900">{shortNumber(stop)}</span>
                        {renderKitchenChip(stop)}
                        {stop.is_preorder && stop.scheduled_for && renderTimeChip(stop)}
                      </div>
                      {stop.customer_name && (
                        <div className="text-sm font-bold text-gray-800 truncate">{stop.customer_name}</div>
                      )}
                      {line1 && <div className="text-xs text-gray-600">{line1}</div>}
                      {line2 && <div className="text-xs text-gray-500">{line2}</div>}
                    </div>
                  </div>
                )
              })}
            </div>
          )}

          {canStartRun(run) ? (
            <button
              onClick={() => onStartRun(run.id)}
              disabled={isStarting}
              className={`mt-3 w-full py-4 text-white text-sm font-black rounded-lg uppercase tracking-wide transition-all ${
                isStarting
                  ? 'bg-indigo-400 cursor-not-allowed'
                  : 'bg-indigo-600 hover:bg-indigo-700 active:scale-95'
              }`}
            >
              {isStarting ? 'Starting…' : 'Start route'}
            </button>
          ) : (
            // Says WHY before the tap — the same condition the API would refuse Start on.
            <div className="mt-3 w-full py-4 px-3 bg-gray-100 text-gray-500 text-sm font-bold rounded-lg text-center">
              {stops.length === 0
                ? 'This route has no stops yet'
                : `Waiting for the kitchen: ${stopsNotReady({ stops }).map(shortNumber).join(', ')}`}
            </div>
          )}
        </div>
      </div>
    )
  }

  // Read-only: another driver's run. No address, no phone — it isn't this driver's job,
  // it's here so an order that left the kitchen is accounted for.
  const renderOtherDriverCard = (order: Order) => (
    <div key={order.id} className="bg-gray-50 rounded-xl border border-gray-200 px-3 py-2 flex items-center justify-between gap-2">
      <span className="text-sm font-black text-gray-500">{shortNumber(order)}</span>
      <span className="inline-flex items-center gap-1 text-xs font-bold text-gray-500 truncate">
        <TruckIcon className="w-3.5 h-3.5 flex-shrink-0" />
        {/* NULL claim = an order flipped out before claim-only went live (the old
            board button stamped nobody). Say so — "another driver" would send
            people asking around; only the counter's board can complete these. */}
        {order.delivered_by_user ? `with ${order.delivered_by_user.name}` : 'unassigned — see counter'}
      </span>
    </div>
  )

  // Lookahead only — not in one of my routes, so no address and no buttons.
  const renderUpcomingCard = (order: Order) => {
    const { line2 } = addressOf(order)
    return (
      <div key={order.id} className="bg-white rounded-xl border border-gray-200 px-3 py-2">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="text-sm font-black text-gray-900">{shortNumber(order)}</span>
          {renderKitchenChip(order)}
          {renderTimeChip(order)}
        </div>
        {line2 && <div className="mt-1 text-xs text-gray-500">{line2}</div>}
      </div>
    )
  }

  return (
    <div className="max-w-xl mx-auto">
      <div className="flex items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <TruckIcon className="w-8 h-8 text-gray-900" />
          <h1 className="text-3xl font-black text-gray-900 tracking-tight">DRIVER</h1>
        </div>
        {(loading || runsLoading) && (
          <div className="flex items-center gap-2 text-sm font-bold text-gray-400 animate-pulse">
            <div className="w-2 h-2 bg-gray-400 rounded-full" />
            UPDATING…
          </div>
        )}
      </div>

      {error && !data && (
        <div className="rounded-xl bg-red-50 border border-red-200 p-4 text-sm font-semibold text-red-800">
          Could not load your deliveries. {error.message}
        </div>
      )}

      {!data && !runsData && !error && (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
        </div>
      )}

      {(data || runsData) && (
        <div className="space-y-8">
          <section>
            {renderSectionHeader('My routes', myRuns.length, 'text-indigo-600')}
            {runsError && !runsData ? (
              // Routes have their own query, so an API without them (or a failed load)
              // costs this section only — the lists below still come from driverDeliveries.
              <div className="rounded-xl bg-red-50 border border-red-200 p-3 text-xs font-semibold text-red-800">
                Routes are not available right now. {runsError.message}
              </div>
            ) : !runsData ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600" />
              </div>
            ) : (
              <div className="space-y-3">
                {myRuns.map(r => (r.status === 'ACTIVE' ? renderActiveRun(r) : renderPlannedRun(r)))}
                {myRuns.length === 0 && renderEmpty('No routes assigned to you')}
              </div>
            )}
          </section>

          {outWithMeUnrouted.length > 0 && (
            <section>
              {renderSectionHeader('Out with you, not in a route', outWithMeUnrouted.length, 'text-indigo-600')}
              <div className="space-y-3">{outWithMeUnrouted.map(order => renderStopCard(null, order))}</div>
            </section>
          )}

          {data && (
            <>
              {/* Purely informational (accounts for orders out on the road with someone
                  else), so it disappears entirely when there's nothing to account for. */}
              {withOthers.length > 0 && (
                <section>
                  {renderSectionHeader('Out with other drivers', withOthers.length, 'text-gray-400')}
                  <div className="space-y-2">{withOthers.map(renderOtherDriverCard)}</div>
                </section>
              )}

              <section>
                <button
                  onClick={() => setShowUpcoming(!showUpcoming)}
                  className="flex items-center gap-1 text-sm font-black uppercase tracking-wide text-blue-600"
                >
                  Coming up ({upcoming.length})
                  {showUpcoming ? (
                    <ChevronDownIcon className="w-4 h-4" />
                  ) : (
                    <ChevronRightIcon className="w-4 h-4" />
                  )}
                </button>
                {showUpcoming && <div className="mt-3 space-y-2">{upcoming.map(renderUpcomingCard)}</div>}
              </section>

              <div className="text-sm font-bold text-gray-400">✓ {deliveredTodayCount} delivered by you today</div>
            </>
          )}
        </div>
      )}
    </div>
  )
}

export default DriverPage
